import type { Enduser, FormField, FormResponseValue } from "@tellescope/types-models";
import { interpolateText, type TemplateContext } from "@tellescope/utils";

export interface SubstituteResponseFieldTextArgs {
  enduser: Partial<Enduser> | null | undefined;
  responses: FormResponseValue[];
  fieldsMap: Record<string, Pick<FormField, "title" | "description" | "htmlDescription">> | undefined;
}

/**
 * Resolves the `{{enduser.*}}` tokens in a response's `fieldTitle` / `fieldDescription` /
 * `fieldHtmlDescription` so the submitted record stores the text the enduser actually read rather
 * than the authored template. Without this a stored response reads back `{{enduser.BMI}}` where the
 * form displayed `28.4`, and the consent language reproduced in a response PDF is the template
 * instead of the patient's own values.
 *
 * Applied at the save/submit boundary rather than to local state: the substitution depends on the
 * whole response set (a token may echo another field's answer), so it can only run once every answer
 * is resolved, and re-running it on each save keeps a token referencing a later answer current.
 *
 * Text is always re-read from the field definition, never from the response's own already-stored
 * value, so repeated saves substitute the original template each time instead of compounding.
 *
 * Substituted values are deliberately **not** entity-escaped here, unlike on the display path: this
 * text is only ever read back through a sanitizer, and escaping at save time would re-encode a value
 * containing `&` on every save.
 */
export const substituteResponseFieldText = <TResponse extends FormResponseValue>(
  response: TResponse,
  { enduser, responses, fieldsMap }: SubstituteResponseFieldTextArgs
): TResponse => {
  const field = fieldsMap?.[response.fieldId];
  if (!field) return response;

  const context: TemplateContext = {
    enduser: enduser ?? null,
    organization: null,
    snippets: {},
    responses,
  };

  const substitute = (value: string | undefined) => (value === undefined ? undefined : interpolateText(value, context));

  const result = { ...response };
  if (field.title !== undefined) result.fieldTitle = substitute(field.title) ?? "";
  if (field.description !== undefined) result.fieldDescription = substitute(field.description);
  if (field.htmlDescription !== undefined) result.fieldHtmlDescription = substitute(field.htmlDescription);

  return result;
};
